import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './AddDistrict.css';

const CreateFormInstance = () => {
  const { masterFormId, subdomain } = useParams();
  const [masterForms, setMasterForms] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [formData, setFormData] = useState({
    master_form_id: masterFormId || '',
    instance_name: '',
    instance_description: '',
    school_year: '',
    version_number: 1,
    district_ids: [],
    display_order: 0 // Default display order
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMasterForms();
    fetchDistricts();
  }, []);

  useEffect(() => {
    if (masterFormId) {
      setFormData(prev => ({ ...prev, master_form_id: masterFormId }));
    }
  }, [masterFormId]);

  useEffect(() => {
    // Auto-select district if subdomain is provided
    if (subdomain && districts.length > 0) {
      const district = districts.find(d => d.subdomain === subdomain);
      if (district) {
        setSelectedDistrict(district);
        setFormData(prev => {
          // Only update if district is not already selected
          if (!prev.district_ids.includes(district.id)) {
            return {
              ...prev,
              district_ids: [district.id]
            };
          }
          return prev;
        });
      } else {
        // District not found - show error and navigate back
        setError(`District with subdomain "${subdomain}" not found`);
        setTimeout(() => {
          navigate('/superadmin/form-instances');
        }, 2000);
      }
    }
  }, [subdomain, districts, navigate]);

  const fetchMasterForms = async () => {
    try {
      const response = await axios.get('/api/forms');
      setMasterForms(response.data);
    } catch (error) {
      console.error('Failed to fetch master forms:', error);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await axios.get('/api/districts');
      setDistricts(response.data);
    } catch (error) {
      console.error('Failed to fetch districts:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'version_number' ? parseInt(value) || 1 : value
    });
  };

  const handleDistrictToggle = (districtId) => {
    setFormData({
      ...formData,
      district_ids: formData.district_ids.includes(districtId)
        ? formData.district_ids.filter(id => id !== districtId)
        : [...formData.district_ids, districtId]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Build district_display_orders object mapping district_id to display_order
      const district_display_orders = {};
      formData.district_ids.forEach(districtId => {
        district_display_orders[districtId] = formData.display_order;
      });

      const payload = {
        ...formData,
        district_display_orders
      };
      
      await axios.post('/api/form-instances', payload);
      // Navigate back to district form instances if subdomain is provided, otherwise to main list
      if (subdomain) {
        navigate(`/superadmin/form-instances/${subdomain}`);
      } else {
        navigate('/superadmin/form-instances');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create form instance');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-district">
      <div style={{ marginBottom: '1rem' }}>
        {subdomain && (
          <button 
            onClick={() => navigate(`/superadmin/form-instances/${subdomain}`)} 
            className="btn-secondary"
            style={{ marginRight: '1rem' }}
          >
            ← Back to {selectedDistrict?.district_name || subdomain}
          </button>
        )}
        <h2 style={{ display: 'inline-block', margin: 0 }}>Create Form Instance</h2>
      </div>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Master Form *</label>
          <select
            name="master_form_id"
            value={formData.master_form_id}
            onChange={handleChange}
            required
            disabled={!!masterFormId}
          >
            <option value="">Select a master form</option>
            {masterForms.map(form => (
              <option key={form.id} value={form.id}>{form.form_name}</option>
            ))}
          </select>
          <small>Select the master form to create an instance from</small>
        </div>

        <div className="form-group">
          <label>Instance Name *</label>
          <input
            type="text"
            name="instance_name"
            value={formData.instance_name}
            onChange={handleChange}
            required
            placeholder="e.g., Student Information Form 2024-2025"
          />
          <small>A unique name for this form instance</small>
        </div>

        <div className="form-group">
          <label>Instance Description</label>
          <textarea
            name="instance_description"
            value={formData.instance_description}
            onChange={handleChange}
            rows="3"
            placeholder="Optional description for this instance"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>School Year</label>
            <input
              type="text"
              name="school_year"
              value={formData.school_year}
              onChange={handleChange}
              placeholder="e.g., 2024-2025"
            />
          </div>

          <div className="form-group">
            <label>Version Number</label>
            <input
              type="number"
              name="version_number"
              value={formData.version_number}
              onChange={handleChange}
              min="1"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Display Order</label>
          <input
            type="number"
            name="display_order"
            value={formData.display_order}
            onChange={handleChange}
            min="0"
          />
          <small>Lower numbers appear first. Set to 1 or higher to appear after New Student Form (which is typically 0).</small>
        </div>

        <div className="form-group">
          <label>Assign to Districts</label>
          {subdomain && selectedDistrict ? (
            <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
              <p style={{ margin: 0, fontWeight: 'bold' }}>
                {selectedDistrict.district_name} ({selectedDistrict.subdomain})
              </p>
              <small style={{ color: '#666' }}>This form instance will be assigned to the current district</small>
            </div>
          ) : (
            <div className="districts-checklist" style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', padding: '1rem', borderRadius: '4px' }}>
              {districts.length === 0 ? (
                <p>No districts available</p>
              ) : (
                districts.map(district => (
                  <label key={district.id} style={{ display: 'block', marginBottom: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.district_ids.includes(district.id)}
                      onChange={() => handleDistrictToggle(district.id)}
                    />
                    {' '}{district.district_name} ({district.subdomain})
                  </label>
                ))
              )}
            </div>
          )}
          <small>Select which districts should have access to this form instance</small>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Instance'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              if (subdomain) {
                navigate(`/superadmin/form-instances/${subdomain}`);
              } else {
                navigate('/superadmin/form-instances');
              }
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateFormInstance;



